import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faSignalSlash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useMemo } from 'react';
import VoxelAutocompleteCity from '../../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteCity';
import VoxelAutocompleteCountry, {
  globalOption,
} from '../../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteCountry';
import VoxelAutocompleteLanguage from '../../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteLanguage';
import { SearchRequestTypes } from '../../../../../components/muiLegacy/Tables/KeywordTable/ToolbarButtons/AddKeywords/AddBasicKeywords/logic/SearchModels';
import VoxelAlert from '../../../../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../../../../components/voxel/Buttons/VoxelButton';
import VoxelCheckbox from '../../../../../components/voxel/Forms/VoxelCheckbox';
import VoxelSelect from '../../../../../components/voxel/Forms/VoxelSelect';
import VoxelLink from '../../../../../components/voxel/Link/VoxelLink';
import VoxelText from '../../../../../components/voxel/Text/VoxelText';
import { GoogleAdsApiRetryDelayGq } from '../../../../../graphql/projects/models/GoogleAdsApiRetryDelayGq';
import { GoogleAdsRequestTypeGq } from '../../../../../graphql/projects/models/GoogleAdsRequestTypeGq';
import { CurrentUserGq } from '../../../../../graphql/users/models/CurrentUserGq';
import {
  KeywordSearchLimits,
  KeywordSearchSeedMultipliers,
  KeywordSearchWarningLimits,
} from '../../../../../logic/otherUtilities/constants/seedInputLimits';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import { useGoogleAdsBlockedInfo } from '../../../../../logic/otherUtilities/useGoogleAdsBlockedInfo';
import { useKeywordCapacityCheck } from '../../../../../logic/otherUtilities/useKeywordCapacityCheck';
import LongTailCheckboxes from '../LongTailCheckboxes';
import SearchTypeInputFields from '../SearchTypeInputFields';
import { validLongTailLanguages } from './logic/QuickSearchModels';
import { setupQuickSearchForm } from './logic/setupQuickSearchForm';

export interface QuickSearchFormProps {
  setRequestId: (id: number, projectId: number) => void;
  user: CurrentUserGq;
  selectedRequestId?: number;
  readOnly?: boolean;
  loading?: boolean;
  blockedQueryResult?: GoogleAdsApiRetryDelayGq;
}

const QuickSearchForm: React.FC<QuickSearchFormProps> = ({
  setRequestId,
  user,
  selectedRequestId,
  readOnly,
  loading,
  blockedQueryResult,
}) => {
  const adsBlockCheck = useGoogleAdsBlockedInfo(blockedQueryResult?.retryAtTime);

  const formBag = setupQuickSearchForm({
    setRequestId,
    user,
  });

  // Keyword warning
  const seedCount = useMemo(
    () => formBag.values.source?.trim()?.split(/[\n,]+/g)?.length,
    [formBag.values.source]
  );
  const seedMultiplier = KeywordSearchSeedMultipliers[formBag.values.requestType];
  const keywordWarningLimit = KeywordSearchWarningLimits[formBag.values.requestType];
  const keywordSearchLimit = KeywordSearchLimits[formBag.values.requestType];

  const keywordCapacity = useMemo(
    () => useKeywordCapacityCheck(user.team, seedCount * seedMultiplier),
    [seedCount]
  );

  const validLongtailLanguage =
    !formBag.values.language?.name ||
    validLongTailLanguages.includes(formBag.values.language?.name);

  const selectedSearchType = SearchRequestTypes.find(
    (rt) => rt?.type === formBag.values.requestType
  );

  return (
    <div className="p-6 pt-4 flex flex-col gap-5 ">
      <div className="w-full flex flex-col gap-4">
        <VoxelSelect
          id={'requestType'}
          size="sm"
          label="Search type"
          labelPosition="top"
          setSelectedOption={(option) =>
            formBag.setFieldValue(
              'requestType',
              SearchRequestTypes.find((type) => type?.type === option.value)?.type
            )
          }
          selectedOptionValue={selectedSearchType?.type}
          options={SearchRequestTypes.filter((t) => t.basic).map((t) => ({
            title: t.name,
            subtitle: t.shortDescription,
            value: t.type,
          }))}
          disabled={formBag.isSubmitting}
        />
        <VoxelAutocompleteLanguage
          type="both"
          selectedLanguage={formBag.values.language}
          setSelectedLanguage={(language) => formBag.setFieldValue('language', language)}
          inputWidthClass="w-full"
          labelPosition="top"
          size="sm"
          disabled={formBag.isSubmitting}
        />
        <VoxelAutocompleteCountry
          type="both"
          selectedCountry={formBag.values.country}
          setSelectedCountry={(country) => formBag.setFieldValue('country', country)}
          clearSelectedCity={() => formBag.setFieldValue('city', null)}
          disabled={formBag.isSubmitting}
          inputWidthClass="w-full"
          labelPosition="top"
          size="sm"
        />
        {formBag.values.country?.id !== globalOption.id && (
          <VoxelAutocompleteCity
            type="both"
            labelPosition="top"
            selectedCountry={formBag.values.country}
            selectedCity={formBag.values.city}
            setSelectedCity={(city) => formBag.setFieldValue('city', city)}
            disabled={formBag.isSubmitting || !formBag.values.country}
            inputWidthClass="w-full"
            size="sm"
          />
        )}
      </div>
      <div className="flex flex-col gap-5">
        <SearchTypeInputFields formik={formBag} readOnly={readOnly} />
        {formBag.values.requestType === GoogleAdsRequestTypeGq.LONG_TAIL && (
          <LongTailCheckboxes
            formik={formBag}
            validLongtailLanguage={validLongtailLanguage}
            disabled={formBag.isSubmitting}
          />
        )}
        <div>
          <VoxelText variant="form-caption" paddingBot>
            More options
          </VoxelText>
          <VoxelCheckbox
            padding
            label="Include zero volume"
            checked={formBag.values.includeZeroVolume}
            onChange={(value: boolean) => formBag.setFieldValue('includeZeroVolume', value)}
            disabled={formBag.isSubmitting}
          />
        </div>
      </div>
      <div className="w-full flex flex-row justify-end">
        <VoxelButton
          title={'Create search'}
          startIcon={<FontAwesomeIcon icon={faSearch} />}
          size="md"
          color="primary"
          submitting={formBag.isSubmitting || (loading && !!selectedRequestId)}
          onClick={() => formBag.submitForm()}
          disabled={
            formBag.isSubmitting ||
            !formBag.dirty ||
            !formBag.isValid ||
            !(formBag.values.source.length > 0) ||
            !user.accessUserCanCreateProject ||
            (!keywordCapacity?.keywordSelectionOk &&
              seedCount > 0 &&
              formBag.values.requestType !== GoogleAdsRequestTypeGq.SITE)
          }
        />
      </div>
      <div>
        {adsBlockCheck?.isBlocked && (
          <VoxelAlert
            severity="warning"
            size="xs"
            fontAIconName={faSignalSlash}
            iconPosition="top"
            iconSize="sm"
            roundedClass="rounded-none first:rounded-t-xl last:rounded-b-xl"
          >
            Our keyword supplier is experiencing issues. The service is expected to be operational{' '}
            <span className="font-semibold">{adsBlockCheck?.blockedUntil}</span>.
            <br />
            <br />
            You may still queue up keywords.
          </VoxelAlert>
        )}
        {seedCount > keywordWarningLimit && !(seedCount > keywordSearchLimit) && (
          <VoxelAlert
            severity="warning"
            size="xs"
            iconSize="lg"
            roundedClass="rounded-none first:rounded-t-xl last:rounded-b-xl"
          >
            Please note that this search may result in up to{' '}
            {formatNumber(seedCount * seedMultiplier)} keywords and that your team has limited
            capacity. Visit{' '}
            <VoxelLink href="/team/usage" openInNewTab openInNewIcon>
              app usage
            </VoxelLink>{' '}
            for more information.
          </VoxelAlert>
        )}
        {!!formBag.errors?.maxCharacters?.length && (
          <VoxelAlert
            severity="error"
            size="xs"
            iconSize="lg"
            roundedClass="rounded-none first:rounded-t-xl last:rounded-b-xl"
          >
            {formBag.errors?.maxCharacters}
          </VoxelAlert>
        )}
        {!!formBag.errors?.source?.length && seedCount > keywordSearchLimit && (
          <VoxelAlert
            severity="error"
            size="xs"
            iconSize="lg"
            roundedClass="rounded-none first:rounded-t-xl last:rounded-b-xl"
          >
            {formBag.errors?.source}
          </VoxelAlert>
        )}
        {!keywordCapacity?.keywordSelectionOk &&
          seedCount > 0 &&
          formBag.values.requestType !== GoogleAdsRequestTypeGq.SITE && (
            <VoxelAlert
              severity="error"
              size="xs"
              iconSize="lg"
              roundedClass="rounded-none first:rounded-t-xl last:rounded-b-xl"
            >
              This search may result in up to {formatNumber(seedCount * seedMultiplier)} keywords
              which is more than what is left of your monthly search volume capacity. Visit{' '}
              <VoxelLink href="/team/usage" openInNewTab openInNewIcon>
                app usage
              </VoxelLink>{' '}
              for more information
            </VoxelAlert>
          )}
      </div>
    </div>
  );
};

export default QuickSearchForm;
